<div class="campaign">
	<div class="title">
		<div>
			<div class="audit">
				@if (campaign.settings.recurrenceType === CampaignRecurrenceType.AFFILIATED) {
					<alaris-tag mode="gray-light" size="lg">{{ 'campaigns.repeat' | translate }}</alaris-tag>
				}
				<span class="note">{{ 'campaigns.createdBy' | translate }} {{ campaign.audit?.createdBy }}</span>
			</div>
			<h3 [alarisTooltip]="campaign.info.name ?? ''" [overflow]="true">
				{{ campaign.info.name }}
			</h3>
			<span class="note">{{ campaign.info.description }}</span>
		</div>
		<div *alarisPBAC="[CP_PERMISSIONS.CAMPAIGNS_E]" class="actions">
			@if (!(campaign.setup.hasOwnProperty('file') && campaign.info.status === CampaignStatus.COMPLETED)) {
				<button
					(click)="action(campaign)"
					alarisButton
					bType="primary"
					data-testid="mainActionBtn"
					size="lg"
					class="actions-button"
				>
					@switch (campaign.info.status) {
						@case (CampaignStatus.SCHEDULED) {
							<alaris-icon class="in-primary" name="icon-play"></alaris-icon>
							{{ 'actions.start' | translate }}
						}
						@case (CampaignStatus.PAUSED) {
							<alaris-icon class="in-primary" name="icon-play"></alaris-icon>
							{{ 'actions.resume' | translate }}
						}
						@case (CampaignStatus.IN_PROGRESS) {
							<alaris-icon class="in-primary" name="icon-pause-circle"></alaris-icon>
							{{ 'actions.pause' | translate }}
						}
						@case (CampaignStatus.WAITING_FOR_APPROVAL) {
							<alaris-icon class="in-primary" name="deprecated/stop"></alaris-icon>
							{{ 'actions.cancel' | translate }}
						}
						@default {
							@if (campaign.setup.hasOwnProperty('file')) {
								<alaris-icon class="in-primary" name="icon-delete"></alaris-icon>
								{{ 'actions.delete' | translate }}
							} @else {
								<alaris-icon class="in-primary" name="icon-arrow-move-list"></alaris-icon>
								{{ (hasPreModeratedCampaignChannels ? 'actions.sendRequest' : 'actions.repeat') | translate }}
							}
						}
					}
				</button>
			}
			<button
				(isOpened)="isOpened = $event"
				[alarisDropdown]="dropdown"
				[positions]="position"
				[class.opened]="isOpened"
				alarisIconButton
				bType="outline"
				class="more-actions"
				data-testid="moreActionsDropdownToggle"
			>
				<alaris-icon name="icon-dot-horizontal"></alaris-icon>
			</button>
			<alaris-dropdown #dropdown>
				<alaris-dropdown-actions [actions]="actions" [row]="campaign" data-testid="dropdownActions">
				</alaris-dropdown-actions>
			</alaris-dropdown>
		</div>
	</div>

	<div class="progress">
		<div class="channels">
			<span class="note">{{ 'gl.channel' | translate }}</span>
			<div class="channels-list">
				@let channels = campaign.setup.channels;
				@for (channel of channels; track $index) {
					<alaris-icon
						[alarisTooltip]="cu.name(channel.channelType) | translate"
						[name]="cu.icon(channel.channelType)"
						class="channel-icon"
					>
					</alaris-icon>
				}
				<div [alarisTooltip]="senderNames" [overflow]="true" class="ellipsis">
					{{ senderNames }}
				</div>
			</div>
		</div>

		<div class="status">
			<span class="note">{{ 'gl.status' | translate }}</span>
			<alaris-tag
				[mode]="campaignUtils.selectStatusColor(campaign.info.status)"
				[fill]="campaign.info.status !== CampaignStatus.SCHEDULED"
				size="lg"
			>
				{{ 'enums.' + campaign.info.status | translate }}
			</alaris-tag>
		</div>

		@if (campaign.info.status === CampaignStatus.IN_PROGRESS || campaign.info.status === CampaignStatus.PAUSED) {
			<div class="bar">
				<span class="note">{{ 'gl.progress' | translate }}</span>
				@let percentageComplete = campaignUtils.calculatePercentage(campaign);
				<alaris-progress-line
					[content]="true"
					[percentageComplete]="percentageComplete"
					[type]="campaignUtils.selectCampaignProgressColor(campaign)"
					class="progress-line"
					labelPosition="bottom"
				>
					<span class="progress-label">
						{{ percentageComplete ? (percentageComplete | number: '1.0-1') + '%' : ('gl.notAvailable' | translate) }}
						<span>
							<span class="progress-label__amount-spent">{{ 'campaigns.amountSpent' | translate }}</span>
							<alaris-cost [cost]="campaign.statistics?.cost" [fade]="false"></alaris-cost>
						</span>
					</span>
				</alaris-progress-line>
			</div>
		}
		@if (
			campaign.info.status === CampaignStatus.CANCELLED &&
			(campaign.declineType === PreModerationDeclineType.BY_SYSTEM || campaign.declineReason)
		) {
			<div class="decline-reason">
				<span class="note">{{ 'campaigns.reasonForDeclined' | translate }}</span>
				<div class="text" [maxWidth]="240" [alarisTooltip]="campaign.declineReason ?? ''">
					{{
						campaign.declineType === PreModerationDeclineType.BY_SYSTEM
							? ('gl.declinedBySystem' | translate)
							: campaign.declineReason
					}}
				</div>
			</div>
		}
	</div>

	<div class="info">
		<div class="contacts">
			@if (campaign.setup.native) {
				@let contacts = campaign.setup.native.contacts;
				<span class="note">{{ 'contacts.title' | translate }}</span>
				@if (contacts.groups === null || contacts.groups?.length) {
					<div class="contacts__groups">
						@if (contacts.groups !== null) {
							@for (groupId of contacts.groups; track groupId) {
								<alaris-tag mode="gray" size="lg">
									{{ (groupId | cGroup)?.name ?? '' | translate }}
									<span class="note">{{ (groupId | cGroup)?.contactsIncluded }}</span>
								</alaris-tag>
							}
						} @else {
							<alaris-tag mode="gray" size="lg">{{ 'gl.all' | translate }}</alaris-tag>
						}
					</div>
				}
				@if (contacts.file !== undefined) {
					<ng-container *ngTemplateOutlet="exportFile; context: { file: contacts.file }"></ng-container>
				}
				@if (contacts.plain !== undefined) {
					<app-campaign-plain-contacts [text]="contacts.plain.text" [code]="contacts.plain.code">
					</app-campaign-plain-contacts>
				}
			}

			@if (campaign.setup.file) {
				@let file = campaign.setup.file.file;
				<ng-container *ngTemplateOutlet="exportFile; context: { file }"></ng-container>
			}

			<ng-template #exportFile let-file="file">
				<button (click)="export(file.id, file.name)" class="file-btn" data-testid="exportBtn">
					<alaris-icon class="file-btn__icon" name="icon-attach"></alaris-icon>
					<div class="file-btn__text">
						<span [alarisTooltip]="file.name" [overflow]="true" class="file-btn__name">
							{{ file.name }}
						</span>
						<span class="file-btn__bytes">{{ formatBytes(file.size) }}</span>
					</div>
				</button>
			</ng-template>
		</div>

		<div class="short-url">
			@if (hasShortUrlSection) {
				<span class="note">{{ 'urlShortener.shortURL' | translate }}</span>
				@let shortLink = campaign.info.shortLink;
				@if (shortLink) {
					<alaris-link
						(click)="navigateToShortener()"
						[alarisTooltip]="shortLink.isArchived ? ('urlShortener.urlBeenArchived' | translate) : ''"
						[underline]="false"
						[attr.data-testid]="'shortLink/' + campaign.id"
						[class.disabled]="shortLink.isArchived"
					>
						{{ shortLink.shortUrl }}
					</alaris-link>
				} @else {
					<span>{{ 'urlShortener.noStatistics' | translate }}</span>
				}
			}
		</div>
		<app-campaign-details-dates
			[campaign]="campaign"
			[isCampaignFromRecurrentHistory]="isCampaignFromRecurrentHistory"
		></app-campaign-details-dates>
	</div>
	<div class="stats">
		<h6 class="stats__title">{{ 'statistics.title' | translate }}</h6>
		<div class="stats__card">
			@let status = campaign.info.status;
			<div class="stats__card__title">
				@let showApproximateCost =
					status === CampaignStatus.SCHEDULED ||
					status === CampaignStatus.IN_PROGRESS ||
					status === CampaignStatus.PAUSED;
				{{ (showApproximateCost ? 'campaigns.approximateCost' : 'campaigns.totalCost') | translate }}
				@if (showApproximateCost) {
					<alaris-icon
						[alarisTooltip]="'campaigns.approximateCostHint' | translate"
						[maxWidth]="270"
						class="pointer"
						name="icon-info"
					>
					</alaris-icon>
				}
			</div>
			<div class="stats__card__amount">
				@let statistics = campaign.statistics;

				@if (
					status === CampaignStatus.SCHEDULED ||
					status === CampaignStatus.IN_PROGRESS ||
					status === CampaignStatus.PAUSED
				) {
					@if (statistics?.preCalculatedCost) {
						<alaris-cost [cost]="statistics?.preCalculatedCost.min"></alaris-cost>
						—
						<alaris-cost [cost]="statistics?.preCalculatedCost.max" [currency]="false"> </alaris-cost>
					}
					@if (!statistics?.preCalculatedCost) {
						—
					}
				} @else {
					<alaris-cost [cost]="statistics?.cost"></alaris-cost>
				}
			</div>
		</div>
		<div class="stats__card">
			<div class="stats__card__title">{{ 'campaigns.totalRecipients' | translate }}</div>
			<h3 class="stats__card__amount">{{ campaign.statistics?.total | number }}</h3>
		</div>
	</div>

	<app-campaign-details-chart [campaign]="campaign" class="chart"></app-campaign-details-chart>

	@if (campaign.setup.channels.length > 1) {
		<div class="cascade-table">
			<h6>{{ 'campaigns.cascadeStep' | translate }}</h6>
			<app-campaign-cascade-table [campaign]="campaign"></app-campaign-cascade-table>
		</div>
	}
</div>

<div class="template">
	@if (tabs.length > 1) {
		<alaris-tabs-button
			[bg]="'darken'"
			[formControl]="tabsControl"
			[tabs]="tabs"
			class="channels"
			data-testid="channelTypeTabs"
			size="md"
		>
		</alaris-tabs-button>
	}
	<app-phone-message
		[buttonActionUrl]="campaignUtils.buttonActionUrl(campaign, tabsControl.value)"
		[buttonCaption]="campaignUtils.buttonCaption(campaign, tabsControl.value)"
		[file]="campaign.setup.file?.file ?? undefined"
		[imageUrl]="campaignUtils.imageUrl(campaign, tabsControl.value)"
		[message]="campaignUtils.message(campaign, tabsControl.value)"
		[sender]="campaignUtils.sender(campaign, tabsControl.value)"
		[shortLink]="campaign.info.shortLink"
		[channelType]="tabsControl.value"
		[whatsappSettings]="campaignUtils.whatsappSettings(campaign, tabsControl.value)"
	>
	</app-phone-message>
</div>
